import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { CandidateCard } from 'features/candidates/components/CandidateCard'
import { EditCandidateModal } from 'features/candidates/components/EditCandidateModal'
import { AddCandidatesModal } from 'features/candidates/components/AddCandidatesModal'
import { DeleteCandidateModal } from 'features/candidates/components/DeleteCandidateModal'
import { Teams } from 'features/teams/pages/Teams'
import { AutoSyncButton } from 'features/candidates/components/AutoSyncButton'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Badge } from 'components/ui/Badge'
import { Card, CardContent } from 'components/ui/Card'
import { Modal } from 'components/ui/Modal'
import { Dropdown } from 'components/ui/Dropdown'
import { PageHeader } from 'components/layout/PageHeader'
import { candidatesApi, teamsApi, type Candidate, type Team } from 'lib/api'
import { translateFrToEn } from 'lib/translationService'
import {
  Plus,
  Search,
  Filter,
  Users,
  Users2,
  UserPlus,
  CheckCircle,
  Grid3x3,
  List,
  Trash2,
  CheckSquare,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNotifications } from 'contexts/NotificationContext'
import { useAuth } from 'contexts/AuthContext'
import { CANDIDATE_FILTER_STEP_CATEGORIES } from 'constants/hiringProcess'

type CandidatesTab = 'candidates' | 'groups'

interface CandidatesProps {
  initialTab?: CandidatesTab
}

export function Candidates({ initialTab = 'candidates' }: CandidatesProps) {
  const { t, i18n } = useTranslation()
  const { addNotification } = useNotifications()
  const { hasPermission, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Check if user is an organization admin
  const isOrgAdmin = user?.is_staff && !user?.is_superuser && user?.organization
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [stats, setStats] = useState({
    total_candidates: 0,
    hired_candidates: 0,
    new_candidates_7_days: 0,
    pending_cvs: 0,
    category_counts: {} as Record<string, number>,
  })
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [groupFilter, setGroupFilter] = useState('all')
  const [teams, setTeams] = useState<Team[]>([])
  const [showCreateTeam, setShowCreateTeam] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDescription, setNewTeamDescription] = useState('')
  const [newTeamColor, setNewTeamColor] = useState('#007bff')
  const [showAddCandidatesModal, setShowAddCandidatesModal] = useState(false)
  const [showEditCandidate, setShowEditCandidate] = useState(false)
  const [creatingTeam, setCreatingTeam] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingCandidate, setDeletingCandidate] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [activeTab, setActiveTab] = useState<CandidatesTab>(initialTab)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20  // Increased from 12 to show more candidates with compact design
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set())
  const [selectionMode, setSelectionMode] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [deletingBulk, setDeletingBulk] = useState(false)
  const [teamsRefreshKey, setTeamsRefreshKey] = useState(0)
  const [translatedRoles, setTranslatedRoles] = useState<Record<number, string>>({})
  const [dupPopoverId, setDupPopoverId] = useState<number | null>(null)
  const translatingCandidates = useRef<Set<number>>(new Set())

  const categoryOptions = ['all', ...CANDIDATE_FILTER_STEP_CATEGORIES]

  const sortByNewestCreated = (a: Candidate, b: Candidate) => {
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0
    if (tb !== ta) return tb - ta
    return (b.id || 0) - (a.id || 0)
  }

  const dedupeCandidatesByEmail = (items: Candidate[]) => {
    const groups = new Map<string, Candidate[]>()
    const noEmail: Candidate[] = []

    // Group candidates by normalized email
    items.forEach(c => {
      const key = (c.email || '').trim().toLowerCase()
      if (!key) {
        noEmail.push(c)
        return
      }
      const arr = groups.get(key) || []
      arr.push(c)
      groups.set(key, arr)
    })

    const visible: Candidate[] = []

    // For each group, keep the most recent and annotate hidden duplicates
    groups.forEach((arr) => {
      const sorted = arr.slice().sort(sortByNewestCreated)
      const head = sorted[0]
      const hidden = sorted.slice(1)
      const annotated = {
        ...head,
        // attach hidden duplicates metadata for UI rendering
        hiddenDuplicates: hidden.map(h => ({ id: h.id, name: h.name, role: h.role })),
      } as Candidate & { hiddenDuplicates?: Array<{ id: number; name: string; role: string }> }
      visible.push(annotated as unknown as Candidate)
    })

    return [...visible, ...noEmail].sort(sortByNewestCreated)
  }

  // Pagination calculations
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const paginatedCandidates = candidates
  
  // Helper to check if user can create candidates (has permission OR is org admin)
  const canCreateCandidate = Boolean(hasPermission('create_candidates') || isOrgAdmin)
  // Helper to check if user can mass import (has permission OR is org admin)
  const canMassImport = Boolean(hasPermission('upload_cvs') || isOrgAdmin)

  const handleOpenAddCandidates = () => {
    if (!canMassImport) {
      addNotification({
        type: 'alert',
        title: t('common.warning'),
        message: t('candidates.addModal.notAvailable'),
        priority: 'medium',
        category: 'cv'
      })
      return
    }
    setShowAddCandidatesModal(true)
  }

  const handleImportComplete = async () => {
    try {
      const params = { page: 1, page_size: itemsPerPage }
      const response = await candidatesApi.getAll(params)
      const uniqueCandidates = dedupeCandidatesByEmail(response.data.results || [])
      setCandidates(uniqueCandidates)
      setTotalItems(response.data.count || 0)
      setCurrentPage(1)
    } catch (error) {
      console.error('Error refreshing candidates:', error)
    }
  }

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, categoryFilter, groupFilter])

  // Fetch Teams once
  const fetchedTeamsRef = useRef(false)
  useEffect(() => {
    if (fetchedTeamsRef.current) return
    fetchedTeamsRef.current = true
    const fetchTeamsData = async () => {
      try {
        // Load all pages — getAll() only returns PAGE_SIZE (20)
        const teamsList = await teamsApi.getAllPages()
        setTeams(teamsList || [])
      } catch (error) {
        console.error('Error fetching teams:', error)
      }
    }
    fetchTeamsData()

    const fetchStatsData = async () => {
      try {
        const statsResponse = await candidatesApi.getStats()
        setStats({
          total_candidates: statsResponse.data.total_candidates || 0,
          hired_candidates: statsResponse.data.hired_candidates || 0,
          new_candidates_7_days: statsResponse.data.new_candidates_7_days || 0,
          pending_cvs: statsResponse.data.pending_cvs || 0,
          category_counts: statsResponse.data.category_counts || {},
        })
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    }
    fetchStatsData()
  }, [teamsRefreshKey]) // Refetch teams if refresh key changes

  // Fetch candidates on page/filter change with debounce
  const candidatesFetchedRef = useRef(false)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const params: any = {
            page: currentPage,
            page_size: itemsPerPage
        }
        if (searchTerm) params.search = searchTerm;
        if (categoryFilter !== 'all') params.step_category = categoryFilter;
        if (groupFilter !== 'all') params.team = groupFilter;

        const candidatesResponse = await candidatesApi.getAll(params)
        // Check dedupe - backend returns raw candidates, we dedupe visible page only safely
        const uniqueCandidates = dedupeCandidatesByEmail(candidatesResponse.data.results || [])
        
        setCandidates(uniqueCandidates)
        setTotalItems(candidatesResponse.data.count || 0)
      } catch (error) {
        console.error('Error fetching candidates:', error)
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load candidates data.',
          duration: 5000,
          priority: 'high'
        })
      } finally {
        setLoading(false)
        candidatesFetchedRef.current = true
      }
    }

    // In StrictMode React mounts -> unmounts -> mounts again, triggering multiple requests
    // We add a short debounce and a lock to prevent that purely for the first mount
    if (!candidatesFetchedRef.current && process.env.NODE_ENV === 'development') {
        fetchData()
        candidatesFetchedRef.current = true
        return
    }

    const timeoutId = setTimeout(() => fetchData(), 300)
    return () => clearTimeout(timeoutId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, categoryFilter, groupFilter, teamsRefreshKey])

  // Helper function to get translated role for a candidate
  const getCandidateRole = (candidate: Candidate) => {
    if (i18n.language === 'en' && translatedRoles[candidate.id]) {
      return translatedRoles[candidate.id]
    }
    return candidate.role
  }

  // Translate candidate roles one by one when language changes to English or page changes
  useEffect(() => {
    const translateRoles = async () => {
      if (i18n.language !== 'en') {
        setTranslatedRoles({})
        translatingCandidates.current.clear()
        return
      }
      
      const candidatesToTranslate = candidates
      
      // Translate only the paginated candidates, one by one
      for (const candidate of candidatesToTranslate) {
        // Skip if already translated or currently translating
        if (!candidate.role || translatedRoles[candidate.id] || translatingCandidates.current.has(candidate.id)) {
          continue
        }
        
        translatingCandidates.current.add(candidate.id)
        
        try {
          const translated = await translateFrToEn(candidate.role)
          setTranslatedRoles(prev => ({ ...prev, [candidate.id]: translated }))
          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100))
        } catch (error) {
          console.error('Translation error for candidate:', error)
          setTranslatedRoles(prev => ({ ...prev, [candidate.id]: candidate.role }))
        } finally {
          translatingCandidates.current.delete(candidate.id)
        }
      }
    }
    
    translateRoles()
  }, [i18n.language, currentPage, candidates])

  const currentPath = `${location.pathname}${location.search}${location.hash}`

  const handleView = (candidate: Candidate) => {
    if (selectionMode && hasPermission('delete_candidates')) {
      toggleSelectCandidate(candidate.id)
    } else {
      navigate(`/candidates/${candidate.id}`, { state: { from: currentPath } })
    }
  }

  const handleEdit = (candidate: Candidate) => {
    setSelectedCandidate(candidate)
    setShowEditCandidate(true)
  }

  const openDeleteModal = (candidate: Candidate) => {
    setSelectedCandidate(candidate)
    setShowDeleteModal(true)
  }

  const handleDeleteCandidate = async () => {
    if (!selectedCandidate) return

    setDeletingCandidate(true)
    try {
      await candidatesApi.delete(selectedCandidate.id)
      setCandidates(candidates.filter(c => c.id !== selectedCandidate.id))
        setTotalItems(prev => prev - 1)
      
      addNotification({
        type: 'update',
        title: t('common.success'),
        message: t('notifications.messages.candidateDeleted', { name: selectedCandidate.name }),
        titleKey: 'common.success',
        messageKey: 'notifications.messages.candidateDeleted',
        messageParams: { name: selectedCandidate.name },
        priority: 'medium',
        category: 'candidate'
      })
      setShowDeleteModal(false)
      setSelectedCandidate(null)
    } catch (error) {
      console.error('Error deleting candidate:', error)
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('notifications.messages.candidateDeleteFailed'),
        titleKey: 'common.error',
        messageKey: 'notifications.messages.candidateDeleteFailed',
        priority: 'high',
        category: 'candidate'
      })
    } finally {
      setDeletingCandidate(false)
    }
  }

  const toggleSelectCandidate = (candidateId: number) => {
    const newSelected = new Set(selectedCandidates)
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId)
    } else {
      newSelected.add(candidateId)
    }
    setSelectedCandidates(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedCandidates.size === paginatedCandidates.length) {
      setSelectedCandidates(new Set())
    } else {
      setSelectedCandidates(new Set(paginatedCandidates.map(c => c.id)))
    }
  }

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode)
    if (selectionMode) {
      // Exit selection mode - clear selections
      setSelectedCandidates(new Set())
    }
  }

  const handleBulkDelete = async () => {
    if (selectedCandidates.size === 0) return

    setDeletingBulk(true)
    try {
      const idsToDelete = Array.from(selectedCandidates)
      const response = await candidatesApi.bulkDelete(idsToDelete)
      
      setCandidates(candidates.filter(c => !selectedCandidates.has(c.id)))
        setTotalItems(prev => prev - idsToDelete.length)
      
      
      addNotification({
        type: 'update',
        title: t('common.success'),
        message: `${response.data.deleted_count} ${t('candidates.candidatesDeleted')}`,
        priority: 'medium',
        category: 'candidate'
      })
      
      setSelectedCandidates(new Set())
      setShowBulkDeleteModal(false)
    } catch (error) {
      console.error('Error deleting candidates:', error)
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('candidates.bulkDeleteFailed'),
        priority: 'high',
        category: 'candidate'
      })
    } finally {
      setDeletingBulk(false)
    }
  }

  const createTeam = async () => {
    if (!newTeamName.trim()) return

    setCreatingTeam(true)
    try {
      // Create the team in the backend
      const teamData = {
        name: newTeamName.trim(),
        description: newTeamDescription.trim(),
        color: newTeamColor,
      }

      const response = await teamsApi.create(teamData)
      const newTeam = response.data

      // Update local state
      setTeams(prev => [newTeam, ...prev])

      // Trigger Teams component refresh
      setTeamsRefreshKey(prev => prev + 1)

      // Reset form
      setNewTeamName('')
      setNewTeamDescription('')
      setNewTeamColor('#007bff')
      setShowCreateTeam(false)

      addNotification({
        type: 'update',
        title: t('common.success'),
        message: t('notifications.messages.teamCreated', { name: newTeam.name }),
        titleKey: 'common.success',
        messageKey: 'notifications.messages.teamCreated',
        messageParams: { name: newTeam.name },
        priority: 'medium',
        category: 'team'
      })

    } catch (error) {
      console.error('Error creating team:', error)

      const errorMessage = error.response?.data?.error || error.message || t('notifications.messages.teamCreateFailed')
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: errorMessage,
        titleKey: 'common.error',
        messageKey: error.response?.data?.error ? undefined : 'notifications.messages.teamCreateFailed',
        priority: 'high',
        category: 'team'
      })
    } finally {
      setCreatingTeam(false)
    }
  }



  return (
    <div className="space-y-4 px-4 py-4">
      <PageHeader
        title={
          activeTab === 'candidates'
            ? t('candidates.title')
            : t('candidateGroups.title', 'Groups')
        }
        actions={
          <div className="flex items-center space-x-2">
          {activeTab === 'candidates' && canMassImport && (
            <>
              <AutoSyncButton onImportStarted={(summary) => {
                handleImportComplete();
              }} />
              <Button onClick={handleOpenAddCandidates} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                <span>{t('candidates.addCandidate')}</span>
              </Button>
            </>
          )}
          {activeTab === 'groups' && (
            <Button onClick={() => setShowCreateTeam(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              <span>{t('candidateGroups.addGroup', 'Add Group')}</span>
            </Button>
          )}
          </div>
        }
      />

      {/* Tabs: Candidates / Groups */}
      <div className="border-b mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('candidates')}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'candidates'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
          >
            <Users className="h-4 w-4 mr-2" />
            {t('candidates.tab.candidates', 'Candidates')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('groups')}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'groups'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
          >
            <Users2 className="h-4 w-4 mr-2" />
            {t('candidates.tab.groups', 'Groups')}
          </button>
        </nav>
      </div>

      {activeTab === 'candidates' && (
        <>
              {/* Stats Cards */}
              <div className="grid gap-3 md:grid-cols-3 sm:grid-cols-2">
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                        <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-xs font-medium text-muted-foreground">{t('candidates.stats.totalCandidates')}</p>
                        <p className="text-xl font-bold">{stats.total_candidates}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-xs font-medium text-muted-foreground">{t('candidates.stats.hiredCandidates')}</p>
                        <p className="text-xl font-bold">{stats.hired_candidates}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
                        <UserPlus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-xs font-medium text-muted-foreground">{t('candidates.stats.newCandidates7Days')}</p>
                        <p className="text-xl font-bold">
                          {stats.new_candidates_7_days}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Condensed Filters */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t('candidates.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  {/* Selection Mode & Bulk Actions (icon-only) */}
                  {hasPermission('delete_candidates') && paginatedCandidates.length > 0 && (
                    <>
                      <Button
                        variant={selectionMode ? "default" : "outline"}
                        size="sm"
                        onClick={toggleSelectionMode}
                        className="whitespace-nowrap transition-all"
                        title={
                          selectionMode
                            ? t('candidates.cancelSelection', 'Cancel selection')
                            : t('candidates.selectMode', 'Select candidates')
                        }
                      >
                        {selectionMode ? (
                          <X className="h-4 w-4" />
                        ) : (
                          <CheckSquare className="h-4 w-4" />
                        )}
                      </Button>
                      {selectionMode && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={toggleSelectAll}
                            className="whitespace-nowrap transition-all"
                            title={
                              selectedCandidates.size === paginatedCandidates.length && paginatedCandidates.length > 0
                                ? t('candidates.deselectAll', 'Deselect all')
                                : t('candidates.selectAll', 'Select all')
                            }
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          {selectedCandidates.size > 0 && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setShowBulkDeleteModal(true)}
                              className="whitespace-nowrap transition-all"
                              title={t('candidates.deleteSelected', { count: selectedCandidates.size, defaultValue: 'Delete selected' })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {/* View Toggle */}
                  <div className="flex items-center gap-0.5 border border-input rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded transition-colors ${viewMode === 'grid'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted'
                        }`}
                      title={t('candidates.gridView')}
                    >
                      <Grid3x3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 rounded transition-colors ${viewMode === 'list'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted'
                        }`}
                      title={t('candidates.listView')}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                  <Dropdown
                    trigger={
                      <Button variant="outline" size="sm" className="whitespace-nowrap">
                        <Filter className="h-4 w-4 mr-2" />
                        <span className="text-sm">{t('candidates.filters')}</span>
                      </Button>
                    }
                  >
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                        {t('candidates.filters')}
                      </p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">{t('candidates.categoryFilter.title', 'Category')}</label>
                          <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none"
                          >
                            {categoryOptions.map((category) => (
                              <option key={category} value={category}>
                                {category === 'all'
                                  ? t('candidates.categoryFilter.all', 'All categories')
                                  : `${t(`hiringProcess.stepTypes.${category}`, category)} (${stats.category_counts?.[category] ?? 0})`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">{t('candidates.group.title', 'Group')}</label>
                          <select
                            value={groupFilter}
                            onChange={(e) => setGroupFilter(e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none"
                          >
                            <option value="all">{t('candidates.group.all')}</option>
                            <option value="ungrouped">{t('candidates.group.ungrouped')}</option>
                            {teams.map((team) => (
                              <option key={team.id} value={team.id.toString()}>
                                {team.name} ({team.candidates?.length || 0})
                              </option>
                            ))}
                          </select>
                        </div>
                        {(searchTerm || categoryFilter !== 'all' || groupFilter !== 'all') && (
                          <div className="pt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSearchTerm('')
                                setCategoryFilter('all')
                                setGroupFilter('all')
                              }}
                              className="w-full"
                            >
                              {t('common.clearFilters', 'Clear filters')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Dropdown>
                </div>
              </div>

              {/* Results */}
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {paginatedCandidates.map((candidate) => (
                    <CandidateCard
                      key={candidate.id}
                      candidate={candidate}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={selectionMode && selectedCandidates.has(candidate.id)}
                      selectionMode={selectionMode && hasPermission('delete_candidates')}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {selectionMode && hasPermission('delete_candidates') && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2 bg-muted/30 border border-border rounded-lg">
                      <CheckSquare className="h-3.5 w-3.5 text-primary" />
                      <span>{t('candidates.selectionModeHint', 'Click on candidates to select them')}</span>
                      {selectedCandidates.size > 0 && (
                        <span className="ml-auto font-medium text-foreground">
                          {selectedCandidates.size} {t('candidates.selected', 'selected')}
                        </span>
                      )}
                    </div>
                  )}
                  {paginatedCandidates.map((candidate) => (
                    <Card
                      key={candidate.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleView(candidate)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleView(candidate)
                        }
                      }}
                      className={`transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                        selectionMode && selectedCandidates.has(candidate.id)
                          ? 'ring-2 ring-primary shadow-md bg-primary/5 border-primary/50'
                          : selectionMode
                          ? 'hover:bg-muted/30 hover:shadow-sm hover:border-primary/20 border-transparent'
                          : 'hover:bg-muted/50 hover:shadow-sm'
                      }`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {candidate.profile_picture ? (
                              <img
                                src={candidate.profile_picture}
                                alt={candidate.name}
                                className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                <Users className="h-5 w-5 text-primary-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <h3 className="font-semibold text-base truncate">{candidate.name}</h3>
                                {((candidate as any).hiddenDuplicates || []).length > 0 && (
                                  <div className="relative inline-block" onClick={(e) => { e.stopPropagation(); setDupPopoverId(id => id === candidate.id ? null : candidate.id) }}>
                                    <Badge variant="outline" className="text-[11px] px-1 py-0 cursor-pointer">
                                      +{(candidate as any).hiddenDuplicates.length} {t('candidates.hiddenDuplicates', { defaultValue: 'autres' })}
                                    </Badge>
                                    {dupPopoverId === candidate.id && (
                                      <div className="absolute z-50 mt-1 w-64 rounded-md border border-border bg-popover text-popover-foreground shadow-md">
                                        <div className="p-2">
                                          <div className="text-[11px] font-medium mb-1">
                                            {t('candidates.hiddenDuplicatesTitle', { defaultValue: 'Candidats masqués' })}
                                          </div>
                                          <ul className="space-y-1">
                                            {(candidate as any).hiddenDuplicates.slice(0, 6).map((dup: any) => (
                                              <li key={dup.id} className="text-[11px] text-muted-foreground truncate">
                                                {dup.name} {dup.role ? `- ${dup.role}` : ''}
                                              </li>
                                            ))}
                                            {(candidate as any).hiddenDuplicates.length > 6 && (
                                              <li className="text-[11px] text-muted-foreground">+{(candidate as any).hiddenDuplicates.length - 6} {t('common.more', { defaultValue: 'plus' })}</li>
                                            )}
                                          </ul>
                                          <div className="mt-2 flex justify-end">
                                            <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); setDupPopoverId(null) }}>
                                              {t('common.close', { defaultValue: 'Fermer' })}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">{getCandidateRole(candidate)}</p>
                              <div className="flex items-center space-x-3 mt-1">
                                <span className="text-xs text-muted-foreground truncate">{candidate.email}</span>
                                {candidate.location && (
                                  <span className="text-xs text-muted-foreground truncate">{candidate.location}</span>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {candidate.status}
                                </Badge>
                              </div>
                              {candidate.bio && (
                                <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2" title={candidate.bio}>
                                  {candidate.bio}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            {hasPermission('edit_candidates') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleEdit(candidate)
                                }}
                                className="h-8 text-xs px-2"
                              >
                                {t('candidates.edit')}
                              </Button>
                            )}
                            {hasPermission('delete_candidates') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  openDeleteModal(candidate)
                                }}
                                className="h-8 text-xs px-2 dark:text-red-400 dark:hover:text-red-300"
                              >
                                {t('candidates.delete')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-3 border-t">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      title={t('common.firstPage', 'First page')}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      title={t('common.previous', 'Previous')}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center px-3 min-w-[3rem] justify-center">
                    <span className="text-xs font-bold text-foreground">
                      {currentPage}/{totalPages}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      title={t('common.next', 'Next')}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      title={t('common.lastPage', 'Last page')}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {totalItems === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-base">
                      {searchTerm || categoryFilter !== 'all' || groupFilter !== 'all'
                        ? t('candidates.noMatchingFilters')
                        : t('candidates.noCandidatesData')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {searchTerm || categoryFilter !== 'all' || groupFilter !== 'all'
                        ? t('candidates.noMatchingFiltersDesc', 'Try adjusting or clearing your filters to find candidates.')
                        : t('candidates.noCandidatesDataDesc', 'Get started by adding a candidate or importing CVs in bulk.')}
                    </p>
                    {!(searchTerm || categoryFilter !== 'all' || groupFilter !== 'all') && canMassImport && (
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <Button onClick={handleOpenAddCandidates} size="sm">
                          <UserPlus className="h-4 w-4 mr-2" />
                          <span>{t('candidates.addCandidate')}</span>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'groups' && (
        // Render the full Teams page inside the Groups tab so all previous functionality is preserved
        <div className="-mx-4 -mb-4">
          <Teams 
            key={teamsRefreshKey}
            hideHeader={true} 
            onCreateTeamClick={() => setShowCreateTeam(true)}
            onTeamCreated={() => setTeamsRefreshKey(prev => prev + 1)}
          />
        </div>
      )}

      <AddCandidatesModal
        isOpen={showAddCandidatesModal}
        onClose={() => setShowAddCandidatesModal(false)}
        canMassImport={canMassImport}
        onImportComplete={handleImportComplete}
      />

      {/* Edit Candidate Modal */}
      <EditCandidateModal
        isOpen={showEditCandidate}
        onClose={() => {
          setShowEditCandidate(false)
          setSelectedCandidate(null)
        }}
        candidate={selectedCandidate}
        onCandidateUpdated={(updatedCandidate) => {
          setCandidates(prev => prev.map(c => c.id === updatedCandidate.id ? updatedCandidate : c))
          setShowEditCandidate(false)
          setSelectedCandidate(null)
        }}
      />

      {/* Delete Candidate Modal */}
      <DeleteCandidateModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedCandidate(null)
        }}
        onConfirm={handleDeleteCandidate}
        candidate={selectedCandidate}
        loading={deletingCandidate}
      />

      {/* Bulk Delete Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        title={t('candidates.bulkDelete.title', 'Delete Multiple Candidates')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('candidates.bulkDelete.message', {
              count: selectedCandidates.size,
              defaultValue: `Are you sure you want to delete ${selectedCandidates.size} candidate(s)? This action cannot be undone.`,
            })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={deletingBulk}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deletingBulk}
            >
              {deletingBulk ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('candidates.bulkDelete.confirm', 'Delete All')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Team Modal */}
      <Modal
        isOpen={showCreateTeam}
        onClose={() => {
          setShowCreateTeam(false)
          setNewTeamName('')
          setNewTeamDescription('')
          setNewTeamColor('#007bff')
        }}
        title={t('candidateGroups.createNewTeam')}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">{t('candidateGroups.teamName')}</label>
            <Input
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder={t('candidateGroups.teamNamePlaceholder')}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('candidateGroups.teamDescription')}</label>
            <Input
              value={newTeamDescription}
              onChange={(e) => setNewTeamDescription(e.target.value)}
              placeholder={t('candidateGroups.teamDescriptionPlaceholder')}
              className="mt-1"
            />
          </div>

          {/* Color Selection */}
          <div>
            <label className="text-sm font-medium mb-2 block">Team Color</label>
            <div className="flex items-center space-x-2">
              {/* Predefined Colors */}
              {[
                '#007bff', '#28a745', '#dc3545', '#ffc107',
                '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'
              ].map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setNewTeamColor(color)}
                  className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${newTeamColor === color ? 'border-gray-800 shadow-lg' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}

              {/* Custom Color */}
              <div className="relative">
                <input
                  type="color"
                  value={newTeamColor}
                  onChange={(e) => setNewTeamColor(e.target.value)}
                  className="w-6 h-6 rounded-full border-2 border-gray-200 cursor-pointer opacity-0 absolute"
                  style={{
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                />
                <div
                  className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${!['#007bff', '#28a745', '#dc3545', '#ffc107',
                      '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'].includes(newTeamColor)
                      ? 'border-gray-800 shadow-lg' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  style={{ backgroundColor: newTeamColor }}
                  title={t('candidates.customColor')}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => {
              setShowCreateTeam(false)
              setNewTeamName('')
              setNewTeamDescription('')
              setNewTeamColor('#007bff')
            }}>
              {t('candidateGroups.cancel')}
            </Button>
            <Button
              onClick={createTeam}
              disabled={!newTeamName.trim() || creatingTeam}
            >
              {creatingTeam ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  {t('candidateGroups.create')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}


